import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView, useSpring, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ArrowUpRight, Sparkles, Layers, Code2, Rocket,
  Play, ChevronRight, ChevronDown, Check, Zap, Globe, Shield, MessageSquare, MapPin,
  ShoppingCart, Brain, Palette, Workflow
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { AnimatedCounter } from '../components/GlobalEffects';

// Local translation helper for component-level translations
const getTranslation = (content, language) => {
  if (content[language]) return content[language];
  if (content['en']) return content['en'];
  if (content['tr']) return content['tr'];
  return content[Object.keys(content)[0]];
};

// Video Assets - Transparent WebM
const PIRONE_VIDEO = "/assets/pirone-logo-video.webm";
const LOADER_VIDEO = "/assets/pirone-loader.webm";

// Animation variants - Enhanced with more dynamic effects
const fadeInUp = {
  hidden: { opacity: 0, y: 80, scale: 0.95 },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: { 
      duration: 0.8, 
      ease: [0.16, 1, 0.3, 1] 
    } 
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      duration: 0.8, 
      ease: [0.16, 1, 0.3, 1] 
    } 
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      duration: 0.8, 
      ease: [0.16, 1, 0.3, 1] 
    } 
  }
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.8, y: 40 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0, 
    transition: { 
      duration: 0.8, 
      ease: [0.16, 1, 0.3, 1] 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      staggerChildren: 0.15, 
      delayChildren: 0.1 
    } 
  }
};

const cardHover = {
  rest: { scale: 1, y: 0 },
  hover: { 
    scale: 1.02, 
    y: -8,
    transition: { 
      duration: 0.4, 
      ease: [0.16, 1, 0.3, 1] 
    } 
  }
};

// Section wrapper with enhanced scroll reveal and parallax
const Section = ({ children, className = '', id, parallax = false }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], parallax ? [80, -80] : [0, 0]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      style={{ y: parallax ? smoothY : 0 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Animated text reveal component
const AnimatedText = ({ children, className = '', delay = 0 }) => {
  return (
    <motion.span
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.span>
  );
};

// Magnetic button effect hook
const useMagnetic = (strength = 0.3) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;
    setPosition({ x: deltaX, y: deltaY });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return { ref, position, handleMouseMove, handleMouseLeave };
};

// ═══════════════════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════════════════
const HeroSection = () => {
  const { language, t } = useLanguage();
  const { scrollY } = useScroll();
  
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 400], [1, 0.98]);

  return (
    <motion.section 
      style={{ opacity: heroOpacity, scale: heroScale }}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-transparent"
    >
      {/* Animated rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {[1, 2, 3].map((ring) => (
          <motion.div
            key={ring}
            className="absolute rounded-full border border-white/[0.03]"
            style={{
              width: `${ring * 300 + 200}px`,
              height: `${ring * 300 + 200}px`,
            }}
            animate={{
              scale: [1, 1.05, 1],
              opacity: [0.3, 0.6, 0.3],
              rotate: ring % 2 === 0 ? [0, 360] : [360, 0],
            }}
            transition={{
              duration: 20 + ring * 5,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>

      {/* Floating shapes */}
      <motion.div
        className="absolute top-20 left-[10%] w-20 h-20 rounded-full bg-gradient-to-br from-[#38BDF8]/20 to-transparent blur-xl"
        animate={{
          y: [0, -30, 0],
          x: [0, 20, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-40 right-[15%] w-16 h-16 rounded-full bg-gradient-to-br from-[#A855F7]/20 to-transparent blur-xl"
        animate={{
          y: [0, 40, 0],
          x: [0, -30, 0],
          scale: [1, 0.8, 1],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div
        className="absolute bottom-40 left-[20%] w-24 h-24 rounded-full bg-gradient-to-br from-[#10B981]/15 to-transparent blur-xl"
        animate={{
          y: [0, -50, 0],
          scale: [1, 1.3, 1],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      <motion.div
        className="absolute bottom-60 right-[10%] w-14 h-14 rounded-full bg-gradient-to-br from-[#F59E0B]/20 to-transparent blur-xl"
        animate={{
          y: [0, 30, 0],
          x: [0, 40, 0],
        }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 3 }}
      />

      {/* Sparkle particles */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-white rounded-full"
          style={{
            left: `${10 + i * 12}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            delay: i * 0.5,
            ease: "easeInOut",
          }}
        />
      ))}
      
      {/* Gradient overlay - smooth top glow */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% 0%, rgba(56, 189, 248, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 20%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)
          `
        }}
      />
      
      {/* Noise texture */}
      <div 
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 text-center pt-32 pb-24">
        <motion.div variants={fadeInUp}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.06] mb-8">
            <div className="w-1.5 h-1.5 rounded-full bg-[#38BDF8] animate-pulse" />
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
              {t('home.badge')}
            </span>
          </div>
        </motion.div>

        {/* Logo */}
        <motion.div variants={fadeInUp} className="mb-6">
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className="relative">
              <video 
                src={PIRONE_VIDEO}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="h-16 sm:h-20 md:h-24 w-auto"
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(56, 189, 248, 0.3))',
                  mixBlendMode: 'lighten',
                  background: 'transparent'
                }}
                onError={(e) => {
                  // Hide video on error and show fallback
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = 'block';
                }}
                onLoadedData={(e) => {
                  // Ensure video is visible when loaded
                  e.target.style.display = 'block';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = 'none';
                }}
              />
              {/* Fallback text logo when video fails */}
              <span 
                className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight hidden"
                style={{ 
                  background: 'linear-gradient(135deg, #38BDF8 0%, #A78BFA 50%, #38BDF8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                PIRONE
              </span>
            </div>
          </div>
          <h1 className="sr-only">{t('home.headline')}</h1>
        </motion.div>

        {/* Subheadline */}
        <motion.p 
          variants={fadeInUp}
          className="text-lg md:text-xl lg:text-2xl text-white/50 leading-relaxed max-w-[700px] mx-auto mb-4 font-light"
        >
          {t('home.subheadline')}
        </motion.p>

        {/* Tagline */}
        <motion.p 
          variants={fadeInUp}
          className="text-sm md:text-base text-[#38BDF8]/70 font-medium mb-12"
        >
          {t('home.tagline')}
        </motion.p>

        {/* CTA Buttons - Dikey Düzen */}
        <motion.div variants={fadeInUp} className="flex flex-col items-center justify-center gap-2">
          {/* Social & Google Growth - Ana Buton */}
          <Link 
            to="/new-jersey"
            className="group relative h-12 px-6 flex items-center gap-3 rounded-full bg-gradient-to-r from-[#38BDF8] via-[#0EA5E9] to-[#0284C7] text-white font-semibold text-sm shadow-[0_0_20px_rgba(56,189,248,0.4)] hover:shadow-[0_0_40px_rgba(56,189,248,0.6)] hover:scale-105 active:scale-95 transition-all duration-300 overflow-hidden"
            data-testid="hero-cta-local"
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <Globe size={16} className="relative z-10" />
            <span className="relative z-10">{t('home.ctaLocal')}</span>
            <ArrowRight size={16} className="relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          
          {/* Açıklama - Butonun altında */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="text-white/50 text-xs sm:text-sm"
          >
            {t('home.ctaLocalDesc')}
          </motion.p>
          
          {/* Create Free Website - Altta */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-2"
          >
            <Link 
              to="/web-design"
              className="group h-10 px-5 flex items-center gap-2 rounded-full bg-white/10 border border-white/20 text-white/70 font-medium text-sm hover:bg-white/20 hover:text-white hover:scale-105 active:scale-95 transition-all duration-300"
              data-testid="hero-cta-primary"
            >
              <Sparkles size={14} />
              <span>{t('home.ctaFreeWebsite')}</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator - Animated */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div 
          className="w-px h-16 bg-gradient-to-b from-white/30 to-transparent"
          animate={{ scaleY: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </motion.section>
  );
};

// ═══════════════════════════════════════════════════════════
// WHAT WE DO SECTION - Premium Interactive Cards
// ═══════════════════════════════════════════════════════════
const WhatWeDoSection = () => {
  const { language } = useLanguage();
  const [expandedCard, setExpandedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const services = [
    // 1. Web Tasarım (sayfa var)
    {
      id: 'web',
      href: '/web-design',
      hasPage: true,
      icon: Code2,
      gradient: 'from-[#38BDF8] to-[#0EA5E9]',
      glowColor: 'rgba(56, 189, 248, 0.4)',
    },
    // 2. Yerel Pazarlama (sayfa var)
    {
      id: 'local',
      href: '/new-jersey',
      hasPage: true,
      icon: MapPin,
      gradient: 'from-[#FB923C] to-[#F97316]',
      glowColor: 'rgba(251, 146, 60, 0.4)',
    },
    // 3. E-Ticaret (expandable)
    {
      id: 'ecommerce',
      hasPage: false,
      icon: ShoppingCart,
      gradient: 'from-[#A78BFA] to-[#8B5CF6]',
      glowColor: 'rgba(167, 139, 250, 0.4)',
    },
    // 4. AI Çözümleri (expandable)
    {
      id: 'ai',
      hasPage: false,
      icon: Brain,
      gradient: 'from-[#34D399] to-[#10B981]',
      glowColor: 'rgba(52, 211, 153, 0.4)',
    },
    // 5. Sosyal Medya (expandable)
    {
      id: 'social',
      hasPage: false,
      icon: MessageSquare,
      gradient: 'from-[#F472B6] to-[#EC4899]',
      glowColor: 'rgba(244, 114, 182, 0.4)',
    },
    // 6. Marka & Tasarım (expandable)
    {
      id: 'branding',
      hasPage: false,
      icon: Palette,
      gradient: 'from-[#FBBF24] to-[#F59E0B]',
      glowColor: 'rgba(251, 191, 36, 0.4)',
    },
  ];

  const content = {
    en: {
      badge: 'What We Do',
      title: 'Transforming Ideas',
      titleHighlight: 'Into Digital Reality',
      clickToExplore: 'Click to explore',
      clickToLearn: 'Click to learn more',
      contactUs: 'Contact Us',
      services: {
        web: { 
          title: 'Web Design', 
          desc: 'Modern, fast websites that convert',
          expanded: null
        },
        local: { 
          title: 'Local Marketing', 
          desc: 'Google Maps visibility & local SEO',
          expanded: null
        },
        ecommerce: { 
          title: 'E-Commerce', 
          desc: 'Powerful online stores',
          expanded: {
            features: [
              'Custom shopping cart & checkout',
              'Payment gateway integration (Stripe, PayPal)',
              'Inventory & order management',
              'Mobile-optimized storefront',
              'Analytics & reporting dashboard'
            ],
            cta: 'Start selling online with a professional e-commerce solution'
          }
        },
        ai: { 
          title: 'AI Solutions', 
          desc: 'Smart automation & chatbots',
          expanded: {
            features: [
              'Custom AI chatbots for customer support',
              'Workflow automation systems',
              'Data analysis & insights',
              'AI-powered content generation',
              'Integration with existing systems'
            ],
            cta: 'Transform your business with intelligent AI solutions'
          }
        },
        social: { 
          title: 'Social Media', 
          desc: 'Grow your online presence',
          expanded: {
            features: [
              'Content strategy & planning',
              'Professional post design & copywriting',
              'Community management & engagement',
              'Paid advertising campaigns (Meta, TikTok)',
              'Analytics & performance reporting'
            ],
            cta: 'Build your brand with strategic social media management'
          }
        },
        branding: { 
          title: 'Branding & Design', 
          desc: 'Memorable brand identity',
          expanded: {
            features: [
              'Logo design & brand identity',
              'Brand guidelines & style guide',
              'Marketing materials & collateral',
              'UI/UX design for digital products',
              'Print design (business cards, brochures)'
            ],
            cta: 'Create a lasting impression with professional branding'
          }
        },
      }
    },
    tr: {
      badge: 'Neler Yapıyoruz',
      title: 'Fikirleri',
      titleHighlight: 'Dijital Gerçeğe Dönüştürüyoruz',
      clickToExplore: 'Keşfetmek için tıklayın',
      clickToLearn: 'Daha fazla bilgi için tıklayın',
      contactUs: 'İletişime Geç',
      services: {
        web: { 
          title: 'Web Tasarım', 
          desc: 'Dönüşüm sağlayan modern, hızlı siteler',
          expanded: null
        },
        local: { 
          title: 'Yerel Pazarlama', 
          desc: 'Google Haritalar ve yerel SEO',
          expanded: null
        },
        ecommerce: { 
          title: 'E-Ticaret', 
          desc: 'Güçlü online mağazalar',
          expanded: {
            features: [
              'Özel alışveriş sepeti ve ödeme sistemi',
              'Ödeme entegrasyonu (Stripe, PayPal, İyzico)',
              'Stok ve sipariş yönetimi',
              'Mobil uyumlu mağaza tasarımı',
              'Analitik ve raporlama paneli'
            ],
            cta: 'Profesyonel e-ticaret çözümüyle online satışa başlayın'
          }
        },
        ai: { 
          title: 'AI Çözümleri', 
          desc: 'Akıllı otomasyon ve chatbotlar',
          expanded: {
            features: [
              'Müşteri desteği için özel AI chatbotlar',
              'İş akışı otomasyon sistemleri',
              'Veri analizi ve içgörüler',
              'AI destekli içerik üretimi',
              'Mevcut sistemlerle entegrasyon'
            ],
            cta: 'Akıllı AI çözümleriyle işinizi dönüştürün'
          }
        },
        social: { 
          title: 'Sosyal Medya', 
          desc: 'Online varlığınızı büyütün',
          expanded: {
            features: [
              'İçerik stratejisi ve planlama',
              'Profesyonel gönderi tasarımı ve metin yazarlığı',
              'Topluluk yönetimi ve etkileşim',
              'Ücretli reklam kampanyaları (Meta, TikTok)',
              'Analitik ve performans raporlama'
            ],
            cta: 'Stratejik sosyal medya yönetimiyle markanızı oluşturun'
          }
        },
        branding: { 
          title: 'Marka & Tasarım', 
          desc: 'Akılda kalıcı marka kimliği',
          expanded: {
            features: [
              'Logo tasarımı ve marka kimliği',
              'Marka kılavuzu ve stil rehberi',
              'Pazarlama materyalleri',
              'Dijital ürünler için UI/UX tasarımı',
              'Baskı tasarımı (kartvizit, broşür)'
            ],
            cta: 'Profesyonel marka kimliğiyle kalıcı bir izlenim bırakın'
          }
        },
      }
    },
    es: {
      badge: 'Lo Que Hacemos',
      title: 'Transformando Ideas',
      titleHighlight: 'En Realidad Digital',
      clickToExplore: 'Clic para explorar',
      clickToLearn: 'Clic para más info',
      contactUs: 'Contáctenos',
      services: {
        web: { title: 'Diseño Web', desc: 'Sitios modernos y rápidos', expanded: null },
        local: { title: 'Marketing Local', desc: 'Visibilidad en Google Maps', expanded: null },
        ecommerce: { 
          title: 'E-Commerce', 
          desc: 'Tiendas online potentes',
          expanded: {
            features: ['Carrito y checkout personalizado', 'Integración de pagos (Stripe, PayPal)', 'Gestión de inventario', 'Diseño móvil optimizado', 'Panel de analytics'],
            cta: 'Comience a vender online con una solución profesional'
          }
        },
        ai: { 
          title: 'Soluciones AI', 
          desc: 'Automatización y chatbots',
          expanded: {
            features: ['Chatbots AI personalizados', 'Sistemas de automatización', 'Análisis de datos', 'Generación de contenido con AI', 'Integración con sistemas existentes'],
            cta: 'Transforme su negocio con soluciones inteligentes'
          }
        },
        social: { 
          title: 'Redes Sociales', 
          desc: 'Crezca su presencia online',
          expanded: {
            features: ['Estrategia de contenido', 'Diseño profesional de posts', 'Gestión de comunidad', 'Campañas de publicidad (Meta, TikTok)', 'Reportes de rendimiento'],
            cta: 'Construya su marca con gestión estratégica'
          }
        },
        branding: { 
          title: 'Marca y Diseño', 
          desc: 'Identidad de marca memorable',
          expanded: {
            features: ['Diseño de logo e identidad', 'Guía de estilo de marca', 'Materiales de marketing', 'Diseño UI/UX', 'Diseño de impresión'],
            cta: 'Cree una impresión duradera con branding profesional'
          }
        },
      }
    },
    pt: {
      badge: 'O Que Fazemos',
      title: 'Transformando Ideias',
      titleHighlight: 'Em Realidade Digital',
      clickToExplore: 'Clique para explorar',
      clickToLearn: 'Clique para mais info',
      contactUs: 'Contate-nos',
      services: {
        web: { title: 'Web Design', desc: 'Sites modernos e rápidos', expanded: null },
        local: { title: 'Marketing Local', desc: 'Visibilidade no Google Maps', expanded: null },
        ecommerce: { 
          title: 'E-Commerce', 
          desc: 'Lojas online poderosas',
          expanded: {
            features: ['Carrinho e checkout personalizado', 'Integração de pagamentos (Stripe, PayPal)', 'Gestão de estoque', 'Design mobile otimizado', 'Painel de analytics'],
            cta: 'Comece a vender online com uma solução profissional'
          }
        },
        ai: { 
          title: 'Soluções AI', 
          desc: 'Automação e chatbots',
          expanded: {
            features: ['Chatbots AI personalizados', 'Sistemas de automação', 'Análise de dados', 'Geração de conteúdo com AI', 'Integração com sistemas existentes'],
            cta: 'Transforme seu negócio com soluções inteligentes'
          }
        },
        social: { 
          title: 'Redes Sociais', 
          desc: 'Cresça sua presença online',
          expanded: {
            features: ['Estratégia de conteúdo', 'Design profissional de posts', 'Gestão de comunidade', 'Campanhas de publicidade (Meta, TikTok)', 'Relatórios de desempenho'],
            cta: 'Construa sua marca com gestão estratégica'
          }
        },
        branding: { 
          title: 'Marca e Design', 
          desc: 'Identidade de marca memorável',
          expanded: {
            features: ['Design de logo e identidade', 'Guia de estilo de marca', 'Materiais de marketing', 'Design UI/UX', 'Design de impressão'],
            cta: 'Crie uma impressão duradoura com branding profissional'
          }
        },
      }
    },
    zh: {
      badge: '我们的服务',
      title: '将创意',
      titleHighlight: '转化为数字现实',
      clickToExplore: '点击探索',
      clickToLearn: '点击了解更多',
      contactUs: '联系我们',
      services: {
        web: { title: '网页设计', desc: '现代快速的网站', expanded: null },
        local: { title: '本地营销', desc: 'Google地图可见性', expanded: null },
        ecommerce: { 
          title: '电子商务', 
          desc: '强大的在线商店',
          expanded: {
            features: ['定制购物车和结账', '支付网关集成 (Stripe, PayPal)', '库存管理', '移动端优化设计', '数据分析面板'],
            cta: '使用专业电商解决方案开始在线销售'
          }
        },
        ai: { 
          title: 'AI解决方案', 
          desc: '智能自动化和聊天机器人',
          expanded: {
            features: ['定制AI客服聊天机器人', '工作流程自动化系统', '数据分析和洞察', 'AI内容生成', '与现有系统集成'],
            cta: '用智能AI解决方案改变您的业务'
          }
        },
        social: { 
          title: '社交媒体', 
          desc: '扩大您的在线影响力',
          expanded: {
            features: ['内容策略和规划', '专业帖子设计', '社区管理和互动', '付费广告活动 (Meta, TikTok)', '数据分析报告'],
            cta: '通过战略性社交媒体管理建立品牌'
          }
        },
        branding: { 
          title: '品牌设计', 
          desc: '令人难忘的品牌形象',
          expanded: {
            features: ['Logo和品牌形象设计', '品牌风格指南', '营销材料设计', 'UI/UX设计', '印刷品设计'],
            cta: '通过专业品牌设计留下持久印象'
          }
        },
      }
    },
    ar: {
      badge: 'ما نفعله',
      title: 'تحويل الأفكار',
      titleHighlight: 'إلى واقع رقمي',
      clickToExplore: 'انقر للاستكشاف',
      clickToLearn: 'انقر لمعرفة المزيد',
      contactUs: 'تواصل معنا',
      services: {
        web: { title: 'تصميم الويب', desc: 'مواقع حديثة وسريعة', expanded: null },
        local: { title: 'التسويق المحلي', desc: 'الظهور في خرائط جوجل', expanded: null },
        ecommerce: { 
          title: 'التجارة الإلكترونية', 
          desc: 'متاجر إلكترونية قوية',
          expanded: {
            features: ['سلة تسوق مخصصة', 'تكامل بوابات الدفع', 'إدارة المخزون', 'تصميم متوافق مع الجوال', 'لوحة التحليلات'],
            cta: 'ابدأ البيع عبر الإنترنت مع حل احترافي'
          }
        },
        ai: { 
          title: 'حلول الذكاء الاصطناعي', 
          desc: 'الأتمتة والروبوتات',
          expanded: {
            features: ['روبوتات دردشة مخصصة', 'أنظمة أتمتة العمل', 'تحليل البيانات', 'إنشاء محتوى بالذكاء الاصطناعي', 'التكامل مع الأنظمة الحالية'],
            cta: 'حوّل عملك بحلول ذكية'
          }
        },
        social: { 
          title: 'وسائل التواصل', 
          desc: 'نمّي تواجدك الرقمي',
          expanded: {
            features: ['استراتيجية المحتوى', 'تصميم منشورات احترافية', 'إدارة المجتمع', 'حملات إعلانية مدفوعة', 'تقارير الأداء'],
            cta: 'ابنِ علامتك التجارية بإدارة استراتيجية'
          }
        },
        branding: { 
          title: 'العلامة التجارية', 
          desc: 'هوية تجارية لا تُنسى',
          expanded: {
            features: ['تصميم الشعار والهوية', 'دليل أسلوب العلامة', 'مواد التسويق', 'تصميم UI/UX', 'تصميم المطبوعات'],
            cta: 'اترك انطباعاً دائماً مع علامة تجارية احترافية'
          }
        },
      }
    },
    hi: {
      badge: 'हम क्या करते हैं',
      title: 'विचारों को',
      titleHighlight: 'डिजिटल वास्तविकता में बदलना',
      clickToExplore: 'एक्सप्लोर करें',
      clickToLearn: 'और जानने के लिए क्लिक करें',
      contactUs: 'संपर्क करें',
      services: {
        web: { title: 'वेब डिज़ाइन', desc: 'आधुनिक, तेज़ वेबसाइट', expanded: null },
        local: { title: 'स्थानीय मार्केटिंग', desc: 'Google Maps विज़िबिलिटी', expanded: null },
        ecommerce: { 
          title: 'ई-कॉमर्स', 
          desc: 'शक्तिशाली ऑनलाइन स्टोर',
          expanded: {
            features: ['कस्टम शॉपिंग कार्ट', 'पेमेंट गेटवे इंटीग्रेशन', 'इन्वेंट्री मैनेजमेंट', 'मोबाइल ऑप्टिमाइज्ड', 'एनालिटिक्स डैशबोर्ड'],
            cta: 'प्रोफेशनल ई-कॉमर्स सॉल्यूशन के साथ ऑनलाइन बेचना शुरू करें'
          }
        },
        ai: { 
          title: 'AI समाधान', 
          desc: 'स्मार्ट ऑटोमेशन और चैटबॉट',
          expanded: {
            features: ['कस्टम AI चैटबॉट', 'वर्कफ़्लो ऑटोमेशन', 'डेटा एनालिसिस', 'AI कंटेंट जनरेशन', 'मौजूदा सिस्टम के साथ इंटीग्रेशन'],
            cta: 'स्मार्ट AI सॉल्यूशंस के साथ अपने बिज़नेस को ट्रांसफॉर्म करें'
          }
        },
        social: { 
          title: 'सोशल मीडिया', 
          desc: 'अपनी ऑनलाइन उपस्थिति बढ़ाएं',
          expanded: {
            features: ['कंटेंट स्ट्रैटेजी', 'प्रोफेशनल पोस्ट डिज़ाइन', 'कम्युनिटी मैनेजमेंट', 'पेड एड कैंपेन', 'परफॉर्मेंस रिपोर्टिंग'],
            cta: 'स्ट्रैटेजिक सोशल मीडिया मैनेजमेंट के साथ ब्रांड बनाएं'
          }
        },
        branding: { 
          title: 'ब्रांडिंग और डिज़ाइन', 
          desc: 'यादगार ब्रांड पहचान',
          expanded: {
            features: ['लोगो और ब्रांड आइडेंटिटी', 'ब्रांड स्टाइल गाइड', 'मार्केटिंग मटेरियल', 'UI/UX डिज़ाइन', 'प्रिंट डिज़ाइन'],
            cta: 'प्रोफेशनल ब्रांडिंग के साथ स्थायी प्रभाव बनाएं'
          }
        },
      }
    },
    ko: {
      badge: '우리가 하는 일',
      title: '아이디어를',
      titleHighlight: '디지털 현실로 변환',
      clickToExplore: '탐색하기',
      clickToLearn: '자세히 알아보기',
      contactUs: '문의하기',
      services: {
        web: { title: '웹 디자인', desc: '현대적이고 빠른 웹사이트', expanded: null },
        local: { title: '로컬 마케팅', desc: 'Google 지도 가시성', expanded: null },
        ecommerce: { 
          title: '이커머스', 
          desc: '강력한 온라인 스토어',
          expanded: {
            features: ['맞춤형 장바구니 및 결제', '결제 게이트웨이 통합', '재고 관리', '모바일 최적화 디자인', '분석 대시보드'],
            cta: '전문 이커머스 솔루션으로 온라인 판매를 시작하세요'
          }
        },
        ai: { 
          title: 'AI 솔루션', 
          desc: '스마트 자동화 및 챗봇',
          expanded: {
            features: ['맞춤형 AI 챗봇', '워크플로우 자동화', '데이터 분석', 'AI 콘텐츠 생성', '기존 시스템과 통합'],
            cta: '스마트 AI 솔루션으로 비즈니스를 혁신하세요'
          }
        },
        social: { 
          title: '소셜 미디어', 
          desc: '온라인 존재감 성장',
          expanded: {
            features: ['콘텐츠 전략 및 기획', '전문적인 포스트 디자인', '커뮤니티 관리', '유료 광고 캠페인', '성과 리포팅'],
            cta: '전략적 소셜 미디어 관리로 브랜드를 구축하세요'
          }
        },
        branding: { 
          title: '브랜딩 & 디자인', 
          desc: '기억에 남는 브랜드 아이덴티티',
          expanded: {
            features: ['로고 및 브랜드 아이덴티티', '브랜드 스타일 가이드', '마케팅 자료', 'UI/UX 디자인', '인쇄물 디자인'],
            cta: '전문적인 브랜딩으로 지속적인 인상을 남기세요'
          }
        },
      }
    },
    el: {
      badge: 'Τι Κάνουμε',
      title: 'Μετατρέποντας Ιδέες',
      titleHighlight: 'Σε Ψηφιακή Πραγματικότητα',
      clickToExplore: 'Κλικ για εξερεύνηση',
      clickToLearn: 'Κλικ για περισσότερα',
      contactUs: 'Επικοινωνήστε',
      services: {
        web: { title: 'Web Design', desc: 'Σύγχρονες, γρήγορες ιστοσελίδες', expanded: null },
        local: { title: 'Τοπικό Marketing', desc: 'Ορατότητα στο Google Maps', expanded: null },
        ecommerce: { 
          title: 'E-Commerce', 
          desc: 'Ισχυρά online καταστήματα',
          expanded: {
            features: ['Προσαρμοσμένο καλάθι αγορών', 'Ενσωμάτωση πληρωμών', 'Διαχείριση αποθέματος', 'Mobile-optimized design', 'Analytics dashboard'],
            cta: 'Ξεκινήστε τις online πωλήσεις με επαγγελματική λύση'
          }
        },
        ai: { 
          title: 'AI Λύσεις', 
          desc: 'Έξυπνη αυτοματοποίηση',
          expanded: {
            features: ['Προσαρμοσμένα AI chatbots', 'Συστήματα αυτοματοποίησης', 'Ανάλυση δεδομένων', 'AI δημιουργία περιεχομένου', 'Ενσωμάτωση με υπάρχοντα συστήματα'],
            cta: 'Μεταμορφώστε την επιχείρησή σας με έξυπνες λύσεις'
          }
        },
        social: { 
          title: 'Social Media', 
          desc: 'Αναπτύξτε την παρουσία σας',
          expanded: {
            features: ['Στρατηγική περιεχομένου', 'Επαγγελματικός σχεδιασμός posts', 'Διαχείριση κοινότητας', 'Διαφημιστικές καμπάνιες', 'Αναφορές απόδοσης'],
            cta: 'Χτίστε το brand σας με στρατηγική διαχείριση'
          }
        },
        branding: { 
          title: 'Branding & Design', 
          desc: 'Αξέχαστη ταυτότητα brand',
          expanded: {
            features: ['Σχεδιασμός logo και ταυτότητας', 'Οδηγός στυλ brand', 'Υλικό marketing', 'UI/UX design', 'Σχεδιασμός εκτυπώσεων'],
            cta: 'Δημιουργήστε διαρκή εντύπωση με επαγγελματικό branding'
          }
        },
      }
    },
    it: {
      badge: 'Cosa Facciamo',
      title: 'Trasformiamo le Idee',
      titleHighlight: 'In Realtà Digitale',
      clickToExplore: 'Clicca per esplorare',
      clickToLearn: 'Clicca per saperne di più',
      contactUs: 'Contattaci',
      services: {
        web: { title: 'Web Design', desc: 'Siti moderni e veloci', expanded: null },
        local: { title: 'Marketing Locale', desc: 'Visibilità su Google Maps', expanded: null },
        ecommerce: { 
          title: 'E-Commerce', 
          desc: 'Negozi online potenti',
          expanded: {
            features: ['Carrello e checkout personalizzato', 'Integrazione gateway di pagamento', 'Gestione inventario', 'Design mobile ottimizzato', 'Dashboard analytics'],
            cta: 'Inizia a vendere online con una soluzione professionale'
          }
        },
        ai: { 
          title: 'Soluzioni AI', 
          desc: 'Automazione e chatbot',
          expanded: {
            features: ['Chatbot AI personalizzati', 'Sistemi di automazione', 'Analisi dei dati', 'Generazione contenuti AI', 'Integrazione con sistemi esistenti'],
            cta: 'Trasforma il tuo business con soluzioni intelligenti'
          }
        },
        social: { 
          title: 'Social Media', 
          desc: 'Cresci la tua presenza online',
          expanded: {
            features: ['Strategia contenuti', 'Design post professionale', 'Gestione community', 'Campagne pubblicitarie', 'Report di performance'],
            cta: 'Costruisci il tuo brand con gestione strategica'
          }
        },
        branding: { 
          title: 'Branding & Design', 
          desc: 'Identità di marca memorabile',
          expanded: {
            features: ['Design logo e identità', 'Guida di stile del brand', 'Materiali di marketing', 'Design UI/UX', 'Design per la stampa'],
            cta: 'Crea un\'impressione duratura con branding professionale'
          }
        },
      }
    },
    gu: {
      badge: 'અમે શું કરીએ છીએ',
      title: 'વિચારોને',
      titleHighlight: 'ડિજિટલ વાસ્તવિકતામાં બદલવું',
      clickToExplore: 'એક્સપ્લોર કરો',
      clickToLearn: 'વધુ જાણવા ક્લિક કરો',
      contactUs: 'સંપર્ક કરો',
      services: {
        web: { title: 'વેબ ડિઝાઇન', desc: 'આધુનિક, ઝડપી વેબસાઇટ્સ', expanded: null },
        local: { title: 'સ્થાનિક માર્કેટિંગ', desc: 'Google Maps વિઝિબિલિટી', expanded: null },
        ecommerce: { 
          title: 'ઈ-કોમર્સ', 
          desc: 'શક્તિશાળી ઓનલાઇન સ્ટોર્સ',
          expanded: {
            features: ['કસ્ટમ શોપિંગ કાર્ટ', 'પેમેન્ટ ગેટવે ઇન્ટિગ્રેશન', 'ઇન્વેન્ટરી મેનેજમેન્ટ', 'મોબાઇલ ઓપ્ટિમાઇઝ્ડ', 'એનાલિટિક્સ ડેશબોર્ડ'],
            cta: 'પ્રોફેશનલ ઈ-કોમર્સ સોલ્યુશન સાથે ઓનલાઇન વેચાણ શરૂ કરો'
          }
        },
        ai: { 
          title: 'AI સોલ્યુશન્સ', 
          desc: 'સ્માર્ટ ઓટોમેશન અને ચેટબોટ્સ',
          expanded: {
            features: ['કસ્ટમ AI ચેટબોટ્સ', 'વર્કફ્લો ઓટોમેશન', 'ડેટા એનાલિસિસ', 'AI કન્ટેન્ટ જનરેશન', 'હાલની સિસ્ટમ્સ સાથે ઇન્ટિગ્રેશન'],
            cta: 'સ્માર્ટ AI સોલ્યુશન્સ સાથે તમારા બિઝનેસને ટ્રાન્સફોર્મ કરો'
          }
        },
        social: { 
          title: 'સોશિયલ મીડિયા', 
          desc: 'તમારી ઓનલાઇન હાજરી વધારો',
          expanded: {
            features: ['કન્ટેન્ટ સ્ટ્રેટેજી', 'પ્રોફેશનલ પોસ્ટ ડિઝાઇન', 'કમ્યુનિટી મેનેજમેન્ટ', 'પેઇડ એડ કેમ્પેઇન', 'પર્ફોર્મન્સ રિપોર્ટિંગ'],
            cta: 'સ્ટ્રેટેજિક સોશિયલ મીડિયા મેનેજમેન્ટ સાથે બ્રાન્ડ બનાવો'
          }
        },
        branding: { 
          title: 'બ્રાન્ડિંગ અને ડિઝાઇન', 
          desc: 'યાદગાર બ્રાન્ડ ઓળખ',
          expanded: {
            features: ['લોગો અને બ્રાન્ડ આઇડેન્ટિટી', 'બ્રાન્ડ સ્ટાઇલ ગાઇડ', 'માર્કેટિંગ મટેરિયલ', 'UI/UX ડિઝાઇન', 'પ્રિન્ટ ડિઝાઇન'],
            cta: 'પ્રોફેશનલ બ્રાન્ડિંગ સાથે કાયમી છાપ બનાવો'
          }
        },
      }
    }
  };

  const c = content[language] || content.en;

  const handleCardClick = (service) => {
    if (service.hasPage) {
      return; // Link will handle navigation
    }
    setExpandedCard(expandedCard === service.id ? null : service.id);
  };

  return (
    <Section id="what-we-do" className="py-28 relative bg-transparent">
      {/* Background */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, #030303 0%, #050508 50%, #030303 100%)'
        }}
      />

      <div className="relative z-10 max-w-[1300px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/[0.08] mb-8">
            <div className="w-2 h-2 rounded-full bg-[#38BDF8] animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">{c.badge}</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
            {c.title}{' '}
            <span className="bg-gradient-to-r from-[#38BDF8] via-[#A78BFA] to-[#38BDF8] bg-clip-text text-transparent">{c.titleHighlight}</span>
          </h2>
        </motion.div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const svc = c.services[service.id];
            const isExpanded = expandedCard === service.id;
            const isHovered = hoveredCard === service.id;
            
            const CardWrapper = service.hasPage ? Link : 'div';
            const cardProps = service.hasPage ? { to: service.href } : { onClick: () => handleCardClick(service) };
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`${isExpanded ? 'lg:col-span-2 md:col-span-2' : ''}`}
              >
                <CardWrapper
                  {...cardProps}
                  className="block cursor-pointer group"
                  onMouseEnter={() => setHoveredCard(service.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <motion.div
                    layout
                    className="relative rounded-3xl overflow-hidden bg-[#0A0A0A] border border-white/[0.06] group-hover:border-white/[0.15] transition-colors"
                    whileHover={!isExpanded ? { scale: 1.02, y: -8 } : {}}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {/* Glow effect */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: `radial-gradient(ellipse at 50% 0%, ${service.glowColor.replace('0.4', '0.15')} 0%, transparent 60%)` }}
                    />
                    
                    {/* Main Content */}
                    <div className="relative z-10 p-7">
                      <div className="flex items-start justify-between mb-4">
                        <motion.div
                          className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg`}
                          animate={isHovered ? { scale: 1.1, rotate: 5 } : { scale: 1, rotate: 0 }}
                          style={{ boxShadow: isHovered ? `0 10px 40px ${service.glowColor}` : 'none' }}
                        >
                          <service.icon size={26} className="text-white" />
                        </motion.div>
                        
                        {!service.hasPage && (
                          <motion.div
                            animate={{ rotate: isExpanded ? 180 : 0 }}
                            className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center"
                          >
                            <ChevronDown size={16} className="text-white/40" />
                          </motion.div>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-2">{svc.title}</h3>
                      <p className="text-white/40 text-sm">{svc.desc}</p>
                      
                      {service.hasPage && (
                        <div className="mt-4 flex items-center gap-2 text-sm font-medium text-white/0 group-hover:text-white/60 transition-colors">
                          <span className={`bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}>{c.clickToExplore}</span>
                          <ArrowRight size={14} />
                        </div>
                      )}
                      
                      {!service.hasPage && !isExpanded && (
                        <div className="mt-4 text-sm text-white/30">{c.clickToLearn}</div>
                      )}
                    </div>
                    
                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && svc.expanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                          className="relative z-10 px-7 pb-7"
                        >
                          <div className="pt-4 border-t border-white/[0.06]">
                            <ul className="space-y-3 mb-6">
                              {svc.expanded.features.map((feature, i) => (
                                <motion.li
                                  key={i}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.1 }}
                                  className="flex items-center gap-3 text-sm text-white/60"
                                >
                                  <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.gradient}`} />
                                  {feature}
                                </motion.li>
                              ))}
                            </ul>
                            
                            <p className="text-white/50 text-sm mb-4">{svc.expanded.cta}</p>
                            
                            <Link
                              to="/contact"
                              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r ${service.gradient} text-white text-sm font-semibold hover:shadow-lg transition-shadow`}
                              style={{ boxShadow: `0 8px 30px ${service.glowColor}` }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {c.contactUs}
                              <ArrowRight size={16} />
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </CardWrapper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// NEW BUSINESS SPECIAL OFFERS SECTION
// ═══════════════════════════════════════════════════════════
const NewBusinessSection = () => {
  const { t } = useLanguage();
  
  // Features with icons - these reference translation keys
  const features = [
    { icon: Globe, titleKey: 'home.newBusinessFeature1Title', descKey: 'home.newBusinessFeature1Desc' },
    { icon: MapPin, titleKey: 'home.newBusinessFeature2Title', descKey: 'home.newBusinessFeature2Desc' },
    { icon: Zap, titleKey: 'home.newBusinessFeature3Title', descKey: 'home.newBusinessFeature3Desc' },
    { icon: Shield, titleKey: 'home.newBusinessFeature4Title', descKey: 'home.newBusinessFeature4Desc' }
  ];

  return (
    <Section className="py-24 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#0a1a10] to-[#030303]" />
      
      {/* Animated glow orbs */}
      <motion.div 
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 60%)' }}
      />
      <motion.div 
        animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, transparent 60%)' }}
      />
      
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <motion.div variants={fadeInUp}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
              <Sparkles size={14} className="text-emerald-400" />
              <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                {t('home.newBusinessBadge')}
              </span>
            </div>

            {/* Title */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">
              {t('home.newBusinessTitle')}
            </h2>
            <p className="text-xl md:text-2xl text-emerald-400 font-medium mb-6">
              {t('home.newBusinessSubtitle')}
            </p>
            
            {/* Description */}
            <p className="text-white/50 leading-relaxed mb-8 max-w-[500px]">
              {t('home.newBusinessDesc')}
            </p>

            {/* Discount Badge */}
            <div className="inline-flex flex-col items-start gap-1 px-6 py-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-8">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-emerald-400">{t('home.newBusinessDiscount')}</span>
              </div>
              <span className="text-emerald-400/60 text-sm">{t('home.newBusinessNote')}</span>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 h-14 px-8 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]"
              >
                <MessageSquare size={18} />
                <span>{t('home.newBusinessCta')}</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                className="group inline-flex items-center justify-center gap-3 h-14 px-8 bg-white/5 border border-white/10 text-white font-semibold rounded-full transition-all duration-300 hover:bg-white/10 hover:border-white/20"
              >
                <span>{t('home.newBusinessCtaSecondary')}</span>
                <ChevronRight size={16} />
              </Link>
            </div>
          </motion.div>

          {/* Right - Features Grid */}
          <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ scale: 1.03, y: -5 }}
                className="relative p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08] group hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-all">
                  <feature.icon size={22} className="text-emerald-400" />
                </div>
                <h4 className="text-white font-semibold mb-2">{t(feature.titleKey)}</h4>
                <p className="text-white/40 text-sm">{t(feature.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// HOW IT WORKS - Pipeline Flow
// ═══════════════════════════════════════════════════════════
const HowItWorksSection = () => {
  const { language } = useLanguage();
  
  const content = {
    en: {
      label: 'Process',
      title: 'How the System Works',
      subtitle: 'From idea to production-ready system in three steps.',
      steps: [
        { num: '01', title: 'Analysis', desc: 'AI-powered requirements scanning and concept creation.', icon: MessageSquare },
        { num: '02', title: 'Architecture', desc: 'System skeleton creation and design approval.', icon: Layers },
        { num: '03', title: 'Build', desc: 'Production-ready code generation and delivery.', icon: Code2 }
      ]
    },
    es: {
      label: 'Proceso',
      title: 'Cómo Funciona el Sistema',
      subtitle: 'De la idea al sistema listo para producción en tres pasos.',
      steps: [
        { num: '01', title: 'Análisis', desc: 'Escaneo de requisitos con IA y creación de conceptos.', icon: MessageSquare },
        { num: '02', title: 'Arquitectura', desc: 'Creación del esqueleto del sistema y aprobación del diseño.', icon: Layers },
        { num: '03', title: 'Construcción', desc: 'Generación de código listo para producción y entrega.', icon: Code2 }
      ]
    },
    tr: {
      label: 'Süreç',
      title: 'Sistem Nasıl Çalışır?',
      subtitle: 'Üç adımda fikirden production-ready sisteme.',
      steps: [
        { num: '01', title: 'Analiz', desc: 'AI destekli gereksinim taraması ve konsept oluşturma.', icon: MessageSquare },
        { num: '02', title: 'Mimari', desc: 'Sistem iskeletinin oluşturulması ve tasarım onayı.', icon: Layers },
        { num: '03', title: 'İnşa', desc: 'Production-ready kod üretimi ve teslim.', icon: Code2 }
      ]
    },
    pt: {
      label: 'Processo',
      title: 'Como o Sistema Funciona',
      subtitle: 'Da ideia ao sistema pronto para produção em três etapas.',
      steps: [
        { num: '01', title: 'Análise', desc: 'Varredura de requisitos com IA e criação de conceitos.', icon: MessageSquare },
        { num: '02', title: 'Arquitetura', desc: 'Criação do esqueleto do sistema e aprovação do design.', icon: Layers },
        { num: '03', title: 'Construção', desc: 'Geração de código pronto para produção e entrega.', icon: Code2 }
      ]
    },
    hi: {
      label: 'प्रक्रिया',
      title: 'सिस्टम कैसे काम करता है',
      subtitle: 'तीन चरणों में विचार से उत्पादन-तैयार सिस्टम तक।',
      steps: [
        { num: '01', title: 'विश्लेषण', desc: 'AI-संचालित आवश्यकता स्कैनिंग और कॉन्सेप्ट निर्माण।', icon: MessageSquare },
        { num: '02', title: 'आर्किटेक्चर', desc: 'सिस्टम स्केलेटन निर्माण और डिज़ाइन अनुमोदन।', icon: Layers },
        { num: '03', title: 'निर्माण', desc: 'प्रोडक्शन-रेडी कोड जनरेशन और डिलीवरी।', icon: Code2 }
      ]
    },
    ko: {
      label: '프로세스',
      title: '시스템 작동 방식',
      subtitle: '세 단계로 아이디어에서 프로덕션 준비 시스템까지.',
      steps: [
        { num: '01', title: '분석', desc: 'AI 기반 요구사항 스캔 및 컨셉 생성.', icon: MessageSquare },
        { num: '02', title: '아키텍처', desc: '시스템 골격 생성 및 디자인 승인.', icon: Layers },
        { num: '03', title: '구축', desc: '프로덕션 준비 코드 생성 및 전달.', icon: Code2 }
      ]
    },
    zh: {
      label: '流程',
      title: '系统如何运作',
      subtitle: '三步从想法到生产就绪系统。',
      steps: [
        { num: '01', title: '分析', desc: 'AI驱动的需求扫描和概念创建。', icon: MessageSquare },
        { num: '02', title: '架构', desc: '系统骨架创建和设计批准。', icon: Layers },
        { num: '03', title: '构建', desc: '生产就绪代码生成和交付。', icon: Code2 }
      ]
    },
    el: {
      label: 'Διαδικασία',
      title: 'Πώς Λειτουργεί το Σύστημα',
      subtitle: 'Από την ιδέα στο έτοιμο σύστημα σε τρία βήματα.',
      steps: [
        { num: '01', title: 'Ανάλυση', desc: 'Σάρωση απαιτήσεων με AI και δημιουργία concept.', icon: MessageSquare },
        { num: '02', title: 'Αρχιτεκτονική', desc: 'Δημιουργία σκελετού συστήματος και έγκριση σχεδιασμού.', icon: Layers },
        { num: '03', title: 'Κατασκευή', desc: 'Παραγωγή κώδικα και παράδοση.', icon: Code2 }
      ]
    },
    ar: {
      label: 'العملية',
      title: 'كيف يعمل النظام',
      subtitle: 'من الفكرة إلى النظام الجاهز للإنتاج في ثلاث خطوات.',
      steps: [
        { num: '01', title: 'التحليل', desc: 'مسح المتطلبات بالذكاء الاصطناعي وإنشاء المفهوم.', icon: MessageSquare },
        { num: '02', title: 'الهندسة', desc: 'إنشاء هيكل النظام والموافقة على التصميم.', icon: Layers },
        { num: '03', title: 'البناء', desc: 'إنشاء الكود الجاهز للإنتاج والتسليم.', icon: Code2 }
      ]
    },
    it: {
      label: 'Processo',
      title: 'Come Funziona il Sistema',
      subtitle: "Dall'idea al sistema pronto per la produzione in tre passaggi.",
      steps: [
        { num: '01', title: 'Analisi', desc: 'Scansione dei requisiti con AI e creazione del concetto.', icon: MessageSquare },
        { num: '02', title: 'Architettura', desc: 'Creazione dello scheletro del sistema e approvazione del design.', icon: Layers },
        { num: '03', title: 'Costruzione', desc: 'Generazione del codice pronto per la produzione e consegna.', icon: Code2 }
      ]
    },
    gu: {
      label: 'પ્રક્રિયા',
      title: 'સિસ્ટમ કેવી રીતે કામ કરે છે',
      subtitle: 'ત્રણ પગલામાં વિચારથી ઉત્પાદન-તૈયાર સિસ્ટમ સુધી.',
      steps: [
        { num: '01', title: 'વિશ્લેષણ', desc: 'AI-સંચાલિત જરૂરિયાત સ્કેનિંગ અને કોન્સેપ્ટ નિર્માણ.', icon: MessageSquare },
        { num: '02', title: 'આર્કિટેક્ચર', desc: 'સિસ્ટમ સ્કેલેટન નિર્માણ અને ડિઝાઇન મંજૂરી.', icon: Layers },
        { num: '03', title: 'નિર્માણ', desc: 'પ્રોડક્શન-રેડી કોડ જનરેશન અને ડિલિવરી.', icon: Code2 }
      ]
    }
  };
  const t = getTranslation(content, language);

  return (
    <Section id="how-it-works" className="py-32 relative bg-transparent">
      {/* Background with smooth gradient */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, #030303 0%, #040406 50%, #030303 100%)'
        }}
      />
      
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-20">
          <span className="inline-block text-[11px] font-mono uppercase tracking-[0.2em] text-[#38BDF8]/60 mb-4">
            {t.label}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 tracking-tight">
            {t.title}
          </h2>
          <p className="text-lg text-white/40 max-w-[500px] mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Steps Pipeline - Consistent hover system */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {t.steps.map((step, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="group relative"
            >
              <div className="pirone-interactive-card p-8 h-full">
                {/* Step number */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-5xl font-bold text-white/[0.06] group-hover:text-[#38BDF8]/20 transition-colors duration-400">
                    {step.num}
                  </span>
                  <div className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center group-hover:border-[#38BDF8]/20 group-hover:bg-[#38BDF8]/5 transition-all duration-400">
                    <step.icon size={20} className="text-white/40 group-hover:text-[#38BDF8] transition-colors duration-400" />
                  </div>
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
                
                {/* Connector line (not on last item) */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 lg:-right-5 w-8 lg:w-10 h-px bg-gradient-to-r from-white/10 to-transparent z-10" />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// AI CONCEPT BUILDER SHOWCASE
// ═══════════════════════════════════════════════════════════
const AIShowcaseSection = () => {
  const { language } = useLanguage();
  const [activePrompt, setActivePrompt] = useState(0);
  
  const content = {
    en: {
      label: 'PIRONE AI',
      title: 'Concept Builder',
      subtitle: 'Describe your idea. AI designs the concept. We deliver professionally.',
      prompts: [
        'Create SaaS dashboard',
        'Design e-commerce site',
        'Build corporate website',
        'Mobile app concept',
        'Restaurant website'
      ],
      cta: 'Generate Concept',
      note: 'These previews cannot be downloaded or published. Work with PIRONE for professional production.',
      features: ['Instant Concept', 'Live Preview', 'Professional Delivery']
    },
    es: {
      label: 'PIRONE AI',
      title: 'Constructor de Conceptos',
      subtitle: 'Describe tu idea. La IA diseña el concepto. Entregamos profesionalmente.',
      prompts: [
        'Crear dashboard SaaS',
        'Diseñar sitio e-commerce',
        'Construir sitio corporativo',
        'Concepto de app móvil',
        'Sitio de restaurante'
      ],
      cta: 'Generar Concepto',
      note: 'Estas previsualizaciones no se pueden descargar ni publicar. Trabaja con PIRONE para producción profesional.',
      features: ['Concepto Instantáneo', 'Vista Previa', 'Entrega Profesional']
    },
    tr: {
      label: 'PIRONE AI',
      title: 'Konsept Oluşturucu',
      subtitle: 'Fikrinizi yazın. AI konsepti tasarlasın. Profesyonel teslim edelim.',
      prompts: [
        'SaaS dashboard oluştur',
        'E-ticaret sitesi tasarla',
        'Kurumsal web sitesi yap',
        'Mobil uygulama konsepti',
        'Restaurant web sitesi'
      ],
      cta: 'Konsept Oluştur',
      note: 'Bu önizlemeler indirilemez veya yayınlanamaz. Profesyonel üretim için PIRONE ile çalışın.',
      features: ['Anında Konsept', 'Canlı Önizleme', 'Profesyonel Teslim']
    },
    pt: {
      label: 'PIRONE AI',
      title: 'Construtor de Conceitos',
      subtitle: 'Descreva sua ideia. A IA projeta o conceito. Entregamos profissionalmente.',
      prompts: [
        'Criar dashboard SaaS',
        'Projetar site e-commerce',
        'Construir site corporativo',
        'Conceito de app móvel',
        'Site de restaurante'
      ],
      cta: 'Gerar Conceito',
      note: 'Estas prévias não podem ser baixadas ou publicadas. Trabalhe com PIRONE para produção profissional.',
      features: ['Conceito Instantâneo', 'Prévia ao Vivo', 'Entrega Profissional']
    },
    hi: {
      label: 'PIRONE AI',
      title: 'कॉन्सेप्ट बिल्डर',
      subtitle: 'अपना विचार बताएं। AI कॉन्सेप्ट डिज़ाइन करे। हम पेशेवर रूप से वितरित करें।',
      prompts: [
        'SaaS डैशबोर्ड बनाएं',
        'ई-कॉमर्स साइट डिज़ाइन करें',
        'कॉर्पोरेट वेबसाइट बनाएं',
        'मोबाइल ऐप कॉन्सेप्ट',
        'रेस्तरां वेबसाइट'
      ],
      cta: 'कॉन्सेप्ट जनरेट करें',
      note: 'ये प्रीव्यू डाउनलोड या प्रकाशित नहीं किए जा सकते। पेशेवर उत्पादन के लिए PIRONE के साथ काम करें।',
      features: ['तत्काल कॉन्सेप्ट', 'लाइव प्रीव्यू', 'पेशेवर डिलीवरी']
    },
    ko: {
      label: 'PIRONE AI',
      title: '컨셉 빌더',
      subtitle: '아이디어를 설명하세요. AI가 컨셉을 디자인합니다. 전문적으로 전달합니다.',
      prompts: [
        'SaaS 대시보드 만들기',
        '이커머스 사이트 디자인',
        '기업 웹사이트 구축',
        '모바일 앱 컨셉',
        '레스토랑 웹사이트'
      ],
      cta: '컨셉 생성',
      note: '이 미리보기는 다운로드하거나 게시할 수 없습니다. 전문 제작을 위해 PIRONE과 함께하세요.',
      features: ['즉시 컨셉', '라이브 미리보기', '전문 전달']
    },
    zh: {
      label: 'PIRONE AI',
      title: '概念构建器',
      subtitle: '描述您的想法。AI设计概念。我们专业交付。',
      prompts: [
        '创建SaaS仪表板',
        '设计电商网站',
        '构建企业网站',
        '移动应用概念',
        '餐厅网站'
      ],
      cta: '生成概念',
      note: '这些预览不能下载或发布。与PIRONE合作进行专业制作。',
      features: ['即时概念', '实时预览', '专业交付']
    },
    el: {
      label: 'PIRONE AI',
      title: 'Δημιουργός Concept',
      subtitle: 'Περιγράψτε την ιδέα σας. Η AI σχεδιάζει το concept. Παραδίδουμε επαγγελματικά.',
      prompts: [
        'Δημιουργία SaaS dashboard',
        'Σχεδιασμός e-commerce',
        'Κατασκευή εταιρικής ιστοσελίδας',
        'Concept mobile app',
        'Ιστοσελίδα εστιατορίου'
      ],
      cta: 'Δημιουργία Concept',
      note: 'Αυτές οι προεπισκοπήσεις δεν μπορούν να ληφθούν ή να δημοσιευθούν. Συνεργαστείτε με την PIRONE για επαγγελματική παραγωγή.',
      features: ['Άμεσο Concept', 'Ζωντανή Προεπισκόπηση', 'Επαγγελματική Παράδοση']
    },
    ar: {
      label: 'PIRONE AI',
      title: 'منشئ المفاهيم',
      subtitle: 'صف فكرتك. الذكاء الاصطناعي يصمم المفهوم. نحن نسلم باحترافية.',
      prompts: [
        'إنشاء لوحة SaaS',
        'تصميم موقع تجارة إلكترونية',
        'بناء موقع شركة',
        'مفهوم تطبيق جوال',
        'موقع مطعم'
      ],
      cta: 'إنشاء مفهوم',
      note: 'لا يمكن تنزيل أو نشر هذه المعاينات. اعمل مع PIRONE للإنتاج الاحترافي.',
      features: ['مفهوم فوري', 'معاينة حية', 'تسليم احترافي']
    },
    it: {
      label: 'PIRONE AI',
      title: 'Costruttore di Concetti',
      subtitle: 'Descrivi la tua idea. L\'AI progetta il concetto. Consegniamo professionalmente.',
      prompts: [
        'Crea dashboard SaaS',
        'Progetta sito e-commerce',
        'Costruisci sito aziendale',
        'Concetto app mobile',
        'Sito ristorante'
      ],
      cta: 'Genera Concetto',
      note: 'Queste anteprime non possono essere scaricate o pubblicate. Lavora con PIRONE per la produzione professionale.',
      features: ['Concetto Istantaneo', 'Anteprima Live', 'Consegna Professionale']
    },
    gu: {
      label: 'PIRONE AI',
      title: 'કોન્સેપ્ટ બિલ્ડર',
      subtitle: 'તમારો વિચાર વર્ણવો. AI કોન્સેપ્ટ ડિઝાઇન કરે. અમે વ્યાવસાયિક રીતે પહોંચાડીએ.',
      prompts: [
        'SaaS ડેશબોર્ડ બનાવો',
        'ઈ-કોમર્સ સાઇટ ડિઝાઇન કરો',
        'કોર્પોરેટ વેબસાઇટ બનાવો',
        'મોબાઇલ એપ કોન્સેપ્ટ',
        'રેસ્ટોરન્ટ વેબસાઇટ'
      ],
      cta: 'કોન્સેપ્ટ જનરેટ કરો',
      note: 'આ પ્રીવ્યૂ ડાઉનલોડ અથવા પ્રકાશિત કરી શકાતા નથી. વ્યાવસાયિક ઉત્પાદન માટે PIRONE સાથે કામ કરો.',
      features: ['ત્વરિત કોન્સેપ્ટ', 'લાઇવ પ્રીવ્યૂ', 'વ્યાવસાયિક ડિલિવરી']
    }
  };
  const t = getTranslation(content, language);

  return (
    <Section id="ai-builder" className="py-32 relative overflow-hidden">
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-transparent" />
      <div 
        className="absolute inset-0 opacity-50"
        style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(56, 189, 248, 0.05) 0%, transparent 50%)'
        }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="inline-block text-[11px] font-mono uppercase tracking-[0.2em] text-[#38BDF8]/60 mb-4">
            {t.label}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 tracking-tight">
            {t.title}
          </h2>
          <p className="text-lg text-white/40 max-w-[600px] mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* IDE-style Interface */}
        <motion.div 
          variants={fadeInUp}
          className="relative rounded-3xl border border-white/[0.06] bg-[#0A0A0A] overflow-hidden"
        >
          {/* Window chrome */}
          <div className="flex items-center gap-2 px-6 py-4 border-b border-white/[0.05]">
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-white/10" />
              <div className="w-3 h-3 rounded-full bg-white/10" />
              <div className="w-3 h-3 rounded-full bg-white/10" />
            </div>
            <div className="flex-1 flex justify-center">
              <span className="text-xs font-mono text-white/20">pirone.ai/concept-builder</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 min-h-[500px]">
            {/* Left: Command Input */}
            <div className="p-8 border-r border-white/[0.05]">
              <div className="mb-6">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 mb-4 block">
                  {language === 'tr' ? 'Konsept Tanımı' : 'Concept Definition'}
                </span>
                <div className="relative">
                  <textarea 
                    placeholder={language === 'tr' ? 'Projenizi tanımlayın...' : 'Describe your project...'}
                    className="w-full h-32 bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#38BDF8]/30 resize-none font-mono transition-colors"
                    data-testid="ai-concept-input"
                  />
                </div>
              </div>

              {/* Quick prompts */}
              <div className="mb-8">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/30 mb-3 block">
                  {language === 'tr' ? 'Hızlı Başlangıç' : 'Quick Start'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {t.prompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => setActivePrompt(i)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-mono transition-all duration-300 ${
                        activePrompt === i 
                          ? 'bg-[#38BDF8]/10 border-[#38BDF8]/30 text-[#38BDF8]' 
                          : 'bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white/60 hover:border-white/10'
                      } border`}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-4 mb-8">
                {t.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/40 text-xs">
                    <Check size={14} className="text-[#38BDF8]" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a 
                href="/concept-builder"
                className="group inline-flex items-center gap-3 h-12 px-6 bg-white text-black font-semibold rounded-full transition-all duration-500 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
                data-testid="ai-showcase-cta"
              >
                <Sparkles size={16} />
                <span>{t.cta}</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Right: Preview mockup */}
            <div className="relative p-8 bg-[#080808] flex items-center justify-center">
              {/* Mockup frame */}
              <div className="relative w-full max-w-[400px]">
                {/* Browser frame */}
                <div className="rounded-xl border border-white/[0.08] bg-[#0C0C0C] overflow-hidden shadow-2xl">
                  {/* Browser bar */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.05]">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="h-5 bg-white/[0.04] rounded-md" />
                    </div>
                  </div>
                  
                  {/* Preview content placeholder */}
                  <div className="p-6 space-y-4 aspect-[4/3]">
                    <div className="h-8 w-1/3 bg-white/[0.04] rounded" />
                    <div className="h-4 w-full bg-white/[0.03] rounded" />
                    <div className="h-4 w-2/3 bg-white/[0.03] rounded" />
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="aspect-square bg-white/[0.03] rounded-lg" />
                      <div className="aspect-square bg-white/[0.03] rounded-lg" />
                      <div className="aspect-square bg-white/[0.03] rounded-lg" />
                    </div>
                    <div className="mt-4 h-10 w-1/3 bg-[#38BDF8]/20 rounded-lg" />
                  </div>
                </div>

                {/* Floating accent */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-[#38BDF8]/10 blur-2xl" />
              </div>

              {/* Note */}
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-[10px] text-white/20 text-center font-mono">
                  {t.note}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// TEMPLATE PREVIEWS - Premium Mockups with 3D Tilt + Light
// ═══════════════════════════════════════════════════════════
const TemplatePreviewsSection = () => {
  const { language } = useLanguage();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });
  
  const templates = [
    { id: 'saas', label: 'SaaS Platform', color: '#38BDF8' },
    { id: 'ecommerce', label: 'E-commerce', color: '#10B981' },
    { id: 'corporate', label: 'Corporate', color: '#8B5CF6' },
    { id: 'restaurant', label: 'Restaurant', color: '#F59E0B' },
    { id: 'agency', label: 'Agency', color: '#EC4899' },
    { id: 'realestate', label: 'Real Estate', color: '#06B6D4' }
  ];

  const content = {
    tr: {
      label: 'Şablonlar',
      title: 'Sistem Kalitesi',
      subtitle: 'Her biri PIRONE standartlarında, production-ready.',
      cta: 'Konsepti Gör'
    },
    en: {
      label: 'Templates',
      title: 'System Quality',
      subtitle: 'Each one at PIRONE standards, production-ready.',
      cta: 'View Concept'
    }
  };
  const t = getTranslation(content, language);

  // Handle mouse move for 3D tilt effect
  const handleMouseMove = (e, cardId) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
    setHoveredCard(cardId);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
    setMousePosition({ x: 0.5, y: 0.5 });
  };

  // Calculate 3D transform
  const getCardStyle = (cardId) => {
    if (hoveredCard !== cardId) {
      return {
        transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
        transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
      };
    }
    
    const rotateX = (mousePosition.y - 0.5) * -12;
    const rotateY = (mousePosition.x - 0.5) * 12;
    
    return {
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`,
      transition: 'transform 0.15s ease-out'
    };
  };

  // Calculate light position
  const getLightStyle = (cardId) => {
    if (hoveredCard !== cardId) {
      return { opacity: 0 };
    }
    
    return {
      opacity: 1,
      background: `radial-gradient(600px circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(56, 189, 248, 0.12), transparent 50%)`
    };
  };

  return (
    <Section id="templates" className="py-32 relative bg-transparent">
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, #030303 0%, #040406 50%, #030303 100%)'
        }}
      />
      
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="inline-block text-[11px] font-mono uppercase tracking-[0.2em] text-[#38BDF8]/60 mb-4">
            {t.label}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 tracking-tight">
            {t.title}
          </h2>
          <p className="text-lg text-white/40 max-w-[500px] mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Template Grid - 3D Tilt + Light Effect */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {templates.map((template, i) => (
            <motion.div
              key={template.id}
              variants={fadeInUp}
              className="group relative"
              style={{ perspective: '1000px' }}
            >
              <div 
                className="relative rounded-2xl lg:rounded-3xl border border-white/[0.05] bg-[#0A0A0A] overflow-hidden"
                style={getCardStyle(template.id)}
                onMouseMove={(e) => handleMouseMove(e, template.id)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Dynamic Light Overlay */}
                <div 
                  className="absolute inset-0 pointer-events-none transition-opacity duration-500 z-10"
                  style={getLightStyle(template.id)}
                />
                
                {/* Ambient Edge Glow */}
                <div 
                  className="absolute inset-0 rounded-2xl lg:rounded-3xl pointer-events-none transition-opacity duration-500 z-0"
                  style={{
                    opacity: hoveredCard === template.id ? 1 : 0,
                    boxShadow: `inset 0 0 60px rgba(56, 189, 248, 0.05), 0 0 40px rgba(56, 189, 248, 0.08)`
                  }}
                />

                {/* Device frame */}
                <div className="relative z-10 p-4 lg:p-6">
                  <div className="rounded-lg lg:rounded-xl border border-white/[0.06] bg-[#080808] overflow-hidden">
                    {/* Browser chrome */}
                    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.04]">
                      <div className="w-2 h-2 rounded-full bg-white/10" />
                      <div className="w-2 h-2 rounded-full bg-white/10" />
                      <div className="w-2 h-2 rounded-full bg-white/10" />
                    </div>
                    
                    {/* Preview content */}
                    <div className="aspect-[4/3] p-4 space-y-3">
                      <div className="h-3 w-1/4 rounded" style={{ backgroundColor: `${template.color}30` }} />
                      <div className="h-2 w-full bg-white/[0.04] rounded" />
                      <div className="h-2 w-3/4 bg-white/[0.03] rounded" />
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div className="aspect-video bg-white/[0.03] rounded" />
                        <div className="aspect-video bg-white/[0.03] rounded" />
                      </div>
                      <div className="h-6 w-20 rounded" style={{ backgroundColor: `${template.color}20` }} />
                    </div>
                  </div>
                </div>

                {/* Label */}
                <div className="relative z-10 px-6 pb-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-white/60 group-hover:text-white transition-colors duration-400">
                    {template.label}
                  </span>
                  <ArrowUpRight size={14} className="text-white/20 group-hover:text-[#38BDF8] transition-colors duration-400" />
                </div>

                {/* Hover overlay with CTA */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-8 z-20">
                  <a 
                    href="/concept-builder"
                    className="px-4 py-2 bg-white text-black text-xs font-semibold rounded-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500"
                  >
                    {t.cta}
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// SERVICES SECTION - Premium Cards with 3D Tilt Effect
// ═══════════════════════════════════════════════════════════
const ServicesSection = () => {
  const { language, t } = useLanguage();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });

  const services = [
    { 
      id: 'website',
      icon: Globe, 
      name: t('home.pricingService1'), 
      priceRange: '$450 – $8,000', 
      desc: t('home.pricingService1Desc'),
      includes: [
        t('home.service1Inc1'),
        t('home.service1Inc2'),
        t('home.service1Inc3')
      ],
      color: 'from-blue-500/5 to-cyan-500/5',
      iconColor: '#38BDF8'
    },
    { 
      id: 'ecommerce',
      icon: ShoppingCart, 
      name: t('home.pricingService2'), 
      priceRange: '$1,500 – $14,000', 
      desc: t('home.pricingService2Desc'),
      includes: [
        t('home.service2Inc1'),
        t('home.service2Inc2'),
        t('home.service2Inc3')
      ],
      color: 'from-purple-500/5 to-pink-500/5',
      iconColor: '#A855F7'
    },
    { 
      id: 'webapp',
      icon: Code2, 
      name: t('home.pricingService3'), 
      priceRange: '$2,500 – $20,000', 
      desc: t('home.pricingService3Desc'),
      includes: [
        t('home.service3Inc1'),
        t('home.service3Inc2'),
        t('home.service3Inc3')
      ],
      color: 'from-emerald-500/5 to-green-500/5',
      iconColor: '#10B981'
    },
    { 
      id: 'mobile',
      icon: Brain, 
      name: t('home.pricingService4'), 
      priceRange: '$5,000 – $38,000', 
      desc: t('home.pricingService4Desc'),
      includes: [
        t('home.service4Inc1'),
        t('home.service4Inc2'),
        t('home.service4Inc3')
      ],
      color: 'from-orange-500/5 to-yellow-500/5',
      iconColor: '#F97316'
    },
    { 
      id: 'ai',
      icon: Palette, 
      name: t('home.pricingService5'), 
      priceRange: '$1,500 – $16,000', 
      desc: t('home.pricingService5Desc'),
      includes: [
        t('home.service5Inc1'),
        t('home.service5Inc2'),
        t('home.service5Inc3')
      ],
      color: 'from-pink-500/5 to-rose-500/5',
      iconColor: '#EC4899'
    },
    { 
      id: 'dashboard',
      icon: Workflow, 
      name: t('home.pricingService6'), 
      priceRange: '$2,500 – $15,000', 
      desc: t('home.pricingService6Desc'),
      includes: [
        t('home.service6Inc1'),
        t('home.service6Inc2'),
        t('home.service6Inc3')
      ],
      color: 'from-indigo-500/5 to-blue-500/5',
      iconColor: '#6366F1'
    }
  ];

  // 3D Tilt Effect handlers
  const handleMouseMove = (e, cardId) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
    setHoveredCard(cardId);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
    setMousePosition({ x: 0.5, y: 0.5 });
  };

  // Calculate 3D transform
  const getCardStyle = (cardId) => {
    if (hoveredCard !== cardId) {
      return {
        transform: 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
        transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)'
      };
    }
    
    const rotateX = (mousePosition.y - 0.5) * -10;
    const rotateY = (mousePosition.x - 0.5) * 10;
    
    return {
      transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`,
      transition: 'transform 0.15s ease-out'
    };
  };

  // Calculate light position
  const getLightStyle = (cardId, iconColor) => {
    if (hoveredCard !== cardId) {
      return { opacity: 0 };
    }
    
    return {
      opacity: 1,
      background: `radial-gradient(400px circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, ${iconColor}20, transparent 50%)`
    };
  };

  return (
    <Section id="services" className="py-24 relative bg-transparent">
      {/* Smooth gradient transition from hero */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            linear-gradient(to bottom, 
              rgba(3, 3, 3, 1) 0%, 
              rgba(5, 5, 5, 0.5) 50%,
              rgba(3, 3, 3, 1) 100%
            ),
            radial-gradient(ellipse 100% 60% at 50% 0%, rgba(56, 189, 248, 0.04) 0%, transparent 60%)
          `
        }}
      />
      
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/20 mb-8">
            <Sparkles size={16} className="text-[#38BDF8]" />
            <span className="text-sm font-medium text-[#38BDF8]">
              {t('home.pricingLabel')}
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold text-white mb-4 tracking-tight">
            {t('home.pricingTitle')}
          </h2>
          <p className="text-lg md:text-xl text-white/50 max-w-[700px] mx-auto">
            {t('home.pricingSubtitle')}
          </p>
        </motion.div>

        {/* Services Grid - Premium Cards with 3D Tilt */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, i) => (
            <motion.div
              key={service.id}
              variants={fadeInUp}
              className="group relative"
              style={{ perspective: '1000px' }}
            >
              <div 
                className={`relative p-6 rounded-2xl bg-gradient-to-br ${service.color} border border-white/[0.08] overflow-hidden h-full`}
                style={getCardStyle(service.id)}
                onMouseMove={(e) => handleMouseMove(e, service.id)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Dynamic Light Overlay */}
                <div 
                  className="absolute inset-0 pointer-events-none transition-opacity duration-500 z-0"
                  style={getLightStyle(service.id, service.iconColor)}
                />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-all duration-300"
                    style={{ 
                      background: `${service.iconColor}10`,
                      border: `1px solid ${service.iconColor}25`
                    }}
                  >
                    <service.icon size={24} style={{ color: service.iconColor }} />
                  </div>
                  
                  {/* Title & Description */}
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {service.name}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed mb-5">
                    {service.desc}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-5">
                    <div className="text-2xl font-bold text-white mb-2">{service.priceRange}</div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                      <Sparkles size={12} className="text-emerald-400" />
                      <span className="text-emerald-400 text-xs font-semibold">{t('home.pricingDiscount')}</span>
                    </span>
                  </div>
                  
                  {/* Includes */}
                  <div className="space-y-2.5">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-white/30">
                      {t('home.includesLabel')}
                    </span>
                    {service.includes.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Check size={14} className="text-[#38BDF8]/60" />
                        <span className="text-white/60 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div variants={fadeInUp} className="text-center">
          <Link
            to="/services"
            className="group inline-flex items-center gap-3 h-14 px-10 bg-white/5 border border-white/10 text-white font-semibold rounded-full transition-all duration-500 hover:bg-white/10 hover:border-white/20 hover:scale-105"
          >
            <span>{t('home.pricingCta')}</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// NEDEN PIRONE? - Why PIRONE Section
// ═══════════════════════════════════════════════════════════
const WhyPironeSection = () => {
  const { t } = useLanguage();
  
  const features = [
    { icon: Zap, titleKey: 'home.whyFeature1Title', descKey: 'home.whyFeature1Desc' },
    { icon: Shield, titleKey: 'home.whyFeature2Title', descKey: 'home.whyFeature2Desc' },
    { icon: Globe, titleKey: 'home.whyFeature3Title', descKey: 'home.whyFeature3Desc' },
    { icon: Rocket, titleKey: 'home.whyFeature4Title', descKey: 'home.whyFeature4Desc' }
  ];

  return (
    <Section id="why-pirone" className="py-32 relative bg-transparent">
      {/* Smooth gradient - slightly darker center */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            linear-gradient(to bottom, 
              rgba(3, 3, 3, 1) 0%, 
              rgba(8, 8, 8, 0.8) 50%,
              rgba(3, 3, 3, 1) 100%
            )
          `
        }}
      />
      
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <span className="inline-block text-[11px] font-mono uppercase tracking-[0.2em] text-[#38BDF8]/60 mb-4">
            {t('home.whyLabel')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 tracking-tight">
            {t('home.whyTitle')}
          </h2>
          <p className="text-lg text-white/40 max-w-[600px] mx-auto">
            {t('home.whySubtitle')}
          </p>
        </motion.div>

        {/* Feature Cards - Enhanced with animations */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              variants={fadeInUp}
              initial="rest"
              whileHover="hover"
              animate="rest"
              className="group cursor-pointer"
            >
              <motion.div 
                variants={{
                  rest: { scale: 1, y: 0 },
                  hover: { scale: 1.03, y: -8 }
                }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="p-6 rounded-2xl bg-[#0A0A0A] border border-white/[0.06] h-full transition-colors duration-400 group-hover:border-[#38BDF8]/30 group-hover:bg-[#0A0A0A]/80 relative overflow-hidden"
              >
                {/* Hover glow effect */}
                <motion.div
                  variants={{
                    rest: { opacity: 0 },
                    hover: { opacity: 1 }
                  }}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle at 50% 0%, rgba(56, 189, 248, 0.1) 0%, transparent 60%)'
                  }}
                />
                
                {/* Icon */}
                <motion.div 
                  variants={{
                    rest: { scale: 1, rotate: 0 },
                    hover: { scale: 1.1, rotate: 5 }
                  }}
                  className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-5 group-hover:border-[#38BDF8]/30 group-hover:bg-[#38BDF8]/10 transition-colors duration-400 relative z-10"
                >
                  <feature.icon size={18} className="text-white/40 group-hover:text-[#38BDF8] transition-colors duration-400" />
                </motion.div>
                
                {/* Content */}
                <h3 className="text-base font-semibold text-white mb-2 group-hover:text-[#38BDF8] transition-colors duration-400 relative z-10">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-white/40 text-sm leading-relaxed group-hover:text-white/60 transition-colors duration-400 relative z-10">
                  {t(feature.descKey)}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// TRUST SECTION
// ═══════════════════════════════════════════════════════════
const TrustSection = () => {
  const { t } = useLanguage();
  
  const stats = [
    { value: '340', suffix: '%', label: t('home.trustStat1Label') },
    { value: '7', suffix: 'x', label: t('home.trustStat2Label') },
    { value: '85', suffix: '%', label: t('home.trustStat3Label') }
  ];

  return (
    <Section id="trust" className="py-32 relative bg-transparent">
      {/* Smooth gradient with subtle center highlight */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% 50%, rgba(56, 189, 248, 0.03) 0%, transparent 60%),
            linear-gradient(to bottom, rgba(3, 3, 3, 1) 0%, rgba(5, 5, 5, 0.6) 50%, rgba(3, 3, 3, 1) 100%)
          `
        }}
      />
      
      <div className="relative z-10 max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
        <motion.div variants={fadeInUp}>
          <span className="inline-block text-[11px] font-mono uppercase tracking-[0.2em] text-[#38BDF8]/60 mb-4">
            {t('home.trustLabel')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-4 tracking-tight">
            {t('home.trustTitle')}
          </h2>
          <p className="text-lg text-white/40 max-w-[500px] mx-auto mb-16">
            {t('home.trustSubtitle')}
          </p>
        </motion.div>

        {/* Stats - Enhanced with hover animations and animated counters */}
        <div className="grid grid-cols-3 gap-8">
          {stats.map((stat, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="group text-center p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] cursor-pointer hover:border-[#38BDF8]/30 hover:bg-white/[0.04] transition-all duration-300 card-glow"
            >
              <motion.span 
                className="block text-4xl md:text-5xl font-bold text-white mb-2 group-hover:text-[#38BDF8] transition-colors duration-300"
              >
                <AnimatedCounter 
                  value={stat.value} 
                  suffix={stat.suffix}
                  duration={2000}
                />
              </motion.span>
              <span className="text-sm text-white/40 group-hover:text-white/60 transition-colors duration-300">{stat.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// CTA SECTION
// ═══════════════════════════════════════════════════════════
const CTASection = () => {
  const { t } = useLanguage();

  return (
    <Section className="py-32 relative bg-transparent">
      {/* Bottom glow for CTA emphasis */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 60% 40% at 50% 80%, rgba(56, 189, 248, 0.06) 0%, transparent 50%),
            linear-gradient(to bottom, rgba(3, 3, 3, 1) 0%, rgba(3, 3, 3, 1) 100%)
          `
        }}
      />
      
      <div className="relative z-10 max-w-[800px] mx-auto px-6 lg:px-12 text-center">
        <motion.div variants={fadeInUp}>
          <motion.h2 
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight"
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            {t('home.ctaTitle')}
          </motion.h2>
          <motion.p 
            className="text-lg text-white/40 mb-10"
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            {t('home.ctaSubtitle')}
          </motion.p>
          <motion.a 
            href="/contact"
            className="group inline-flex items-center gap-3 h-14 px-10 bg-white text-black font-semibold rounded-full transition-all duration-300"
            data-testid="cta-final"
            whileHover={{ scale: 1.08, boxShadow: '0 0 60px rgba(255,255,255,0.4)' }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span>{t('home.ctaCta')}</span>
            <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-300" />
          </motion.a>
        </motion.div>
      </div>
    </Section>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
const HomePage = () => {
  return (
    <div className="min-h-screen bg-transparent relative">
      <main>
        <HeroSection />
        <WhatWeDoSection />
        <TrustSection />
        <CTASection />
      </main>
    </div>
  );
};

export default HomePage;
